import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { StatusBar, Style } from '@capacitor/status-bar';
import React from 'react';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';
import { flushSync } from 'react-dom';
import i18next from 'i18next';
import { joinClassNames } from '@famly/stat_ts-utils_join-class-names';
import { ModernFamlyContext } from '@famly/mf_system_provider';
import PlatformHelper from 'web-app/helpers/platform-helper';
import Button from 'web-app/react/components/form-elements/button/button';
import { shallowEqualIgnoreFunctions } from '@famly/vet_ts-utils_generic-hooks';
import Close from 'web-app/react/components/icons/legacy/lazy-icons/close';
import { Base } from 'web-app/react/components/layout/layout';
import { PortalRootId } from 'web-app/react/components/portals/constants';
import Portal from 'web-app/react/components/portals/portal';
import ProfileImage from 'web-app/react/components/profile-image/profile-image';
import { CLASSNAME_PREFIX } from 'web-app/react/global-styles/legacy-modal-style';
import * as ModalsContainerActions from 'web-app/react/modals-container/actions';
import { renderStyle } from 'web-app/styleguide/render-style';
import { s0, s2 } from 'web-app/styleguide/spacing';
import { media } from 'web-app/styleguide/utils';
import * as CapacitorUtil from 'web-app/util/capacitor';
import { hasValue } from '@famly/vet_ts-utils_typescript';
import { Text } from '@famly/mf_data-display_text';
import { ModalContent } from './modal-styled';
// The React Compiler cannot lower a template-literal computed object key, so these
// static class names are hoisted to module scope and used as plain identifier keys
// (which compile). They only depend on the module-level `CLASSNAME_PREFIX`.
const showCancelButtonClass = `${CLASSNAME_PREFIX}showCancelButton`;
const hasRightMobileElementClass = `${CLASSNAME_PREFIX}hasRightMobileElement`;
const hasLeftMobileElementClass = `${CLASSNAME_PREFIX}hasLeftMobileElement`;
const whiteClass = `${CLASSNAME_PREFIX}white`;
const fullScreenOnMobileClass = `${CLASSNAME_PREFIX}fullScreenOnMobile`;
const fixHeaderOnMobileClass = `${CLASSNAME_PREFIX}fixHeaderOnMobile`;
const fitToViewportClass = `${CLASSNAME_PREFIX}fitToViewport`;
export const ModalBody = styled.div `
    padding: 0 16px 16px 16px;

    ${props => props.noPadding
    ? css `
                  padding: 0;
              `
    : ''}

    ${props => props.mobileScroll
    ? media.mobile `
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
    `
    : ''}
`;
const FullWidthBase = styled(Base) `
    max-width: 100%;
`;
class ModalHeaderClass extends React.PureComponent {
    render() {
        const { header, subHeader, image, headlineProps } = this.props;
        return [
            image ? (_jsx("div", { className: `${CLASSNAME_PREFIX}modalTopBar_image`, children: _jsx("div", { className: `${CLASSNAME_PREFIX}modalHeaderImage`, children: _jsx(ProfileImage, { src: image }) }) }, "modalHeaderImage")) : null,
            _jsxs(FullWidthBase, { paddingLeft: image ? s2 : s0, children: [header ? (
                    // Explicit color is required: the legacy `.modalTopBar` global CSS sets
                    // `color: white`, which the MF `Text` would otherwise inherit — rendering the
                    // header invisible on the (white) top bar background.
                    _jsx(Text, { variant: "h6", component: "h3", emphasized: true, color: "n500", ...headlineProps, "data-e2e-class": "modal-header", children: header })) : null, subHeader ? (_jsx(Text, { variant: "body-small", component: "span", color: "n200", "data-e2e-class": "modal-subheader", children: subHeader })) : null] }, "modalHeader"),
        ];
    }
}
export const ModalHeader = React.memo(ModalHeaderClass, shallowEqualIgnoreFunctions);
export const ModalCloseButton = (props) => {
    return (_jsx(Button, { appearance: "custom", className: `${CLASSNAME_PREFIX}outsideCloseButton`, onClick: props.onCloseModal, children: _jsx(Close, { className: `${CLASSNAME_PREFIX}outsideCloseIcon`, appearance: "white" }) }));
};
const StyledModalTopBar = styled.div `
    &&& {
        background: ${props => props.theme.invertedText};
        box-shadow: none;
        .${`${CLASSNAME_PREFIX}closeButtonContainer`} {
            align-self: flex-start;
        }
    }
`;
const StyledCloseButton = styled(Close).attrs(() => ({
    appearance: 'default',
})) ``;
const StyledModalHeaderButton = styled(Button) `
    display: block;
    margin: 0;
    font-family: ${props => props.theme.fontConfiguration.family};
    font-size: 0;
    line-height: 0;
    letter-spacing: normal;
    color: ${props => (props.$isWhite ? props.theme.invertedText : props.theme.text)};
`;
export const ModalHeaderButton = ({ isWhite, className, children, onClick, }) => (_jsx(StyledModalHeaderButton, { buttonSize: 'mini', appearance: "icon", className: className, "$isWhite": isWhite, onClick: onClick, children: _jsx(Text, { variant: "h6", component: "h3", children: children }) }));
export const ModalTopBar = (props) => {
    const { className, supportsCancel, leftMobileElement, rightMobileElement, onCloseModal, children, background } = props;
    const isWhite = background === 'white';
    const showCancelButton = supportsCancel && onCloseModal;
    return (_jsxs(StyledModalTopBar, { className: joinClassNames(`${CLASSNAME_PREFIX}modalTopBar`, className, showCancelButton && showCancelButtonClass, hasValue(rightMobileElement) && hasRightMobileElementClass, (hasValue(leftMobileElement) || showCancelButton) && hasLeftMobileElementClass, isWhite && whiteClass), children: [showCancelButton && !leftMobileElement ? (_jsx("div", { className: `${CLASSNAME_PREFIX}cancelButtonContainer`, onClick: onCloseModal, children: _jsx(ModalHeaderButton, { isWhite: isWhite, children: i18next.t($ => $.cancel) }) })) : null, leftMobileElement ? (_jsx("div", { className: `${CLASSNAME_PREFIX}leftMobileElement`, children: leftMobileElement })) : null, children ? _jsx("div", { className: `${CLASSNAME_PREFIX}modalTopBar_children`, children: children }) : null, onCloseModal ? (_jsx("div", { className: `${CLASSNAME_PREFIX}closeButtonContainer`, onClick: onCloseModal, children: _jsx(Button, { id: "closeModalButton", buttonSize: 'mini', appearance: "icon", className: `${CLASSNAME_PREFIX}closeButton`, "data-e2e-class": "modalCloseButton", children: _jsx(StyledCloseButton, { className: `${CLASSNAME_PREFIX}closeIcon` }) }) })) : null, rightMobileElement ? (_jsx("div", { className: `${CLASSNAME_PREFIX}rightMobileElement`, children: rightMobileElement })) : null] }));
};
/**
 * Portal target for dropdown menus that must escape a modal's scroll-clipping and stacking context.
 *
 * Both offsets must stay pinned: react-select positions a portalled menu with the control's
 * viewport coordinates, so this layer has to be the containing block that starts at the viewport
 * origin. Leaving `left` to the layer's static position offsets every menu by wherever the layer
 * happens to sit in the DOM.
 */
export const TopLayer = styled.div `
    position: fixed;
    top: 0;
    left: 0;
    z-index: 9999;
`;
export class BaseBackdrop extends React.Component {
    constructor(props) {
        super(props);
        this.handleMouseUp = this.handleMouseUp.bind(this);
        this.handleMouseDown = this.handleMouseDown.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.state = {
            clickState: 'awaitingMouseEvent',
        };
    }
    render() {
        const { onClick } = this.props;
        const propsToApply = {
            ...this.props,
            onMouseUp: onClick && this.handleMouseUp,
            onMouseDown: onClick && this.handleMouseDown,
            onClick: onClick && this.handleClick,
        };
        return (_jsx(StyledBackdrop, { ...propsToApply, className: this.props.className, children: this.props.children }));
    }
    /*
        We introduced event handlers to mitigate an issue where you could mousedown inside a modal, move the mouse onto the
        backdrop, and then release it, which would then fire a click event on the backdrop and close it.
        In a similar way you could mousedown on the backdrop, move the mouse inside the modal, and then release it,
        which would also fire a click event on the backdrop and then closing the modal.

        Part of our strategy to circumvent this problem add state that keeps track of which events had been received
        by the backdrop. Only in the case where we get `mousedown` -> `mouseup` -> `onclick` will be actually call
        the `onClick` prop passed to this component. If our event handlers catch any other sequence they should
        reset the state back to it's initial value.
     */
    handleMouseUp(e) {
        e.stopPropagation();
        if (PlatformHelper.isMobileApp()) {
            return;
        }
        else if (this.state.clickState === 'receivedMouseDown') {
            flushSync(() => this.setState({ clickState: 'receivedMouseUpAfterMouseDown' }));
        }
        else {
            flushSync(() => this.setState({ clickState: 'awaitingMouseEvent' }));
        }
    }
    handleMouseDown(e) {
        e.stopPropagation();
        if (PlatformHelper.isMobileApp()) {
            return;
        }
        else if (this.state.clickState === 'awaitingMouseEvent') {
            flushSync(() => this.setState({ clickState: 'receivedMouseDown' }));
        }
    }
    handleClick(e) {
        e.stopPropagation();
        if (PlatformHelper.isMobileApp() && this.props.onClick) {
            this.props.onClick(e);
        }
        else if (this.state.clickState === 'receivedMouseUpAfterMouseDown' && this.props.onClick) {
            this.props.onClick(e);
        }
        flushSync(() => this.setState({ clickState: 'awaitingMouseEvent' }));
    }
}
const getStackNumber = (layerOrder, overrideZIndex) => {
    if (layerOrder) {
        return 120 - layerOrder + 1;
    }
    else if (overrideZIndex) {
        return overrideZIndex;
    }
    return 120;
};
export const StyledBackdrop = styled.div.attrs(props => ({
    className: joinClassNames('modalBackdrop', `${CLASSNAME_PREFIX}groupActionModalBackdrop`, props.className),
})) `
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: ${props => getStackNumber(props.layerOrder, props.overrideZIndex)};
    background-color: ${props => props.theme.blackScrim};
    ${props => props.noOverflow
    ? css `
                  overflow: hidden;
              `
    : ''}

    ${props => props.noPadding
    ? css `
                  padding: ${props => props.theme.mf.spacing(0)};
              `
    : ''}

    ${props => (props.containerCSS ? props.containerCSS : '')};
`;
export const Backdrop = React.memo(BaseBackdrop, shallowEqualIgnoreFunctions);
const WhenModalIsOpenCSS = renderStyle(css `
    body {
        overflow: hidden;
    }
`);
const typeClassMap = {
    tiny: `${CLASSNAME_PREFIX}tiny`,
    medium: `${CLASSNAME_PREFIX}medium`,
    big: `${CLASSNAME_PREFIX}big`,
    navigation: `${CLASSNAME_PREFIX}navModal`,
    fullscreen: `${CLASSNAME_PREFIX}fullscreen`,
    custom: `${CLASSNAME_PREFIX}custom`,
};
export const MODAL_PORTAL_DROPDOWN_ROOT_ID = 'modal-dropdown-portal-root';
/*
    This was introduced to mitigate an issue where you could mousedown inside a modal, move the mouse onto the
    backdrop, and then release it, which would then fire a click event on the backdrop and close it.
    In a similar way you could mousedown on the backdrop, move the mouse inside the modal, and then release it,
    which would also fire a click event on the backdrop and then closing the modal.

    Part of our strategy to circumvent this problem is to stop mouse events from propagating from inside the modal out
    to the backdrop.

    Check the Backdrop class component to see the rest of the implementation introduced in an effort to fix this
 */
const stopEventPropagation = (e) => {
    e.stopPropagation();
};
const InnerModal = ({ onCloseModal, containerClassName, children, type, className, fixHeaderOnMobile, fullScreenOnMobile = true, id, fitToViewport, noBackdropOverflow, layerOrder, noBackdrop, overrideZIndex, noPadding, 'data-e2e-id': dataE2eId, }) => {
    const dispatch = useDispatch();
    const modernFamlyContext = React.use(ModernFamlyContext);
    React.useEffect(() => {
        dispatch(ModalsContainerActions.modalOpened());
        return () => {
            dispatch(ModalsContainerActions.modalClosed());
        };
    }, [dispatch]);
    React.useEffect(() => {
        if (!(PlatformHelper.hasDynamicStatusBar() && PlatformHelper.isMobileSize() && fullScreenOnMobile)) {
            return;
        }
        let shouldResetStatusBarOnUnmount = false;
        StatusBar.getInfo().then(info => {
            /**
             * The reason for not always wanting to "reset" the status bar on unmount,
             * is that we want to preserve the black status bar text when multiple fullscreen modals are stacked on top of each other.
             */
            if (info.style === Style.Dark) {
                shouldResetStatusBarOnUnmount = true;
            }
            CapacitorUtil.setStatusBar(Style.Light);
        });
        return () => {
            if (shouldResetStatusBarOnUnmount) {
                CapacitorUtil.setStatusBar(Style.Dark);
            }
        };
    }, [fullScreenOnMobile]);
    const handleClose = (e) => {
        stopEventPropagation(e);
        if (onCloseModal) {
            onCloseModal();
        }
    };
    // Due to the way MUI has implemented, we need to temporarily "detach" these listeners when a datepicker's "calendar popper" is open.
    // Otherwise, the "calendar popper" cannot be closed again due to not receiving the clicks.
    const listeners = (modernFamlyContext === null || modernFamlyContext === void 0 ? void 0 : modernFamlyContext.isDisplayingDatePicker)
        ? {}
        : {
            onMouseDown: stopEventPropagation,
            onMouseUp: stopEventPropagation,
            onClick: stopEventPropagation,
        };
    const content = (_jsx(ModalContent, { "data-e2e-id": dataE2eId, type: type === 'fullscreen' ? 'fullscreen' : undefined, id: id, ...listeners, className: joinClassNames(`${CLASSNAME_PREFIX}groupActionModal`, 'clearfix', fullScreenOnMobile && fullScreenOnMobileClass, fixHeaderOnMobile && fixHeaderOnMobileClass, fitToViewport && fitToViewportClass, type ? typeClassMap[type] : undefined, className), children: children }));
    return (_jsxs(React.Fragment, { children: [_jsx(TopLayer, { id: MODAL_PORTAL_DROPDOWN_ROOT_ID }), _jsx(WhenModalIsOpenCSS, {}), noBackdrop ? (content) : (_jsx(Backdrop, { className: containerClassName, onClick: 
                // Due to the way MUI has implemented, we need to temporarily "detach" this callback when a datepicker's "calendar popper" is open.
                // Otherwise, the "calendar popper" cannot be closed again due to not receiving the clicks.
                !(modernFamlyContext === null || modernFamlyContext === void 0 ? void 0 : modernFamlyContext.isDisplayingDatePicker) && Boolean(onCloseModal) ? handleClose : undefined, noOverflow: noBackdropOverflow, layerOrder: layerOrder, overrideZIndex: overrideZIndex, noPadding: noPadding, children: content }))] }));
};
const StyledInnerModal = styled(InnerModal).attrs(props => ({
    suppressClassNameWarning: true,
    className: joinClassNames(props.className, 'inner-modal'),
})) `
    /* This follows the same breakpoint as in the styles for when we have full screen modal */
    ${props => props.fullScreenOnMobile
    ? css `
                  @media all and (max-width: 50em) {
                      padding-top: env(safe-area-inset-top);
                  }
              `
    : ''}

    border-radius: ${props => props.theme.containerConfiguration.borderRadius};

    .${`${CLASSNAME_PREFIX}modalTopBar`} {
        border-top-left-radius: ${props => props.theme.containerConfiguration.borderRadius};
        border-top-right-radius: ${props => props.theme.containerConfiguration.borderRadius};
    }

    ${props => props.hideOverflow
    ? css `
                  overflow: hidden;
              `
    : ''}
`;
export const Modal = React.memo(StyledInnerModal, shallowEqualIgnoreFunctions);
export const PortalModal = (props) => (_jsx(Portal, { rootId: props.rootId || PortalRootId.modalRoot, children: _jsx(Modal, { ...props }) }));
